import type {
  IPutDocumentReaderPartParams,
  TPutDocumentReaderPartResult,
} from './types';

const CORS_ETAG_ERROR =
  'The part reached storage but its ETag header could not be read. The bucket CORS must expose ETag to this site before data rooms can upload.';
const NETWORK_ERROR =
  'The part could not reach storage. Check the connection; if it keeps failing, the bucket CORS may not allow uploads from this site.';
const CANCELLED_ERROR = 'The upload was cancelled.';

/** PUTs one part to its signed URL. XMLHttpRequest rather than fetch, because only it reports upload
 * progress. No headers are added, so the signature the backend made still matches. */
export function putDocumentReaderPart({
  url,
  blob,
  onProgress,
  signal,
}: Readonly<IPutDocumentReaderPartParams>): Promise<TPutDocumentReaderPartResult> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve({ success: false, status: 0, error: CANCELLED_ERROR });
      return;
    }

    const xhr = new XMLHttpRequest();
    const onAbortSignal = () => xhr.abort();
    const settle = (result: TPutDocumentReaderPartResult) => {
      signal?.removeEventListener('abort', onAbortSignal);
      resolve(result);
    };

    signal?.addEventListener('abort', onAbortSignal, { once: true });
    xhr.open('PUT', url);
    xhr.upload.onprogress = (event) => onProgress?.(event.loaded);
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        settle({
          success: false,
          status: xhr.status,
          error: `Storage refused the part (status ${xhr.status}).`,
        });
        return;
      }
      // Null unless the bucket CORS lists ETag in ExposeHeaders; a part without it is never counted.
      const entityTag = xhr.getResponseHeader('ETag');
      if (!entityTag) {
        settle({ success: false, status: xhr.status, error: CORS_ETAG_ERROR });
        return;
      }
      settle({ success: true, entityTag });
    };
    xhr.onerror = () =>
      settle({ success: false, status: 0, error: NETWORK_ERROR });
    xhr.onabort = () =>
      settle({ success: false, status: 0, error: CANCELLED_ERROR });
    xhr.send(blob);
  });
}
